'use client';

import { useTranslation } from '@/components/translation';
import { PropsWithChildren, ReactElement, useEffect, useState } from 'react';
import styles from './appLayout.module.scss';
import { AlertProvider, YourStore, withFeatureFlagProvider, AppHeader, AppFooter } from '@cvs-web/splash-components';
import { AppEnvName, isMobileApp } from '@cvs-web/splash-components/server';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { environment, isHealth100Client } from '@/lib/utils/utils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

if (process?.env?.ENV === 'local') {
  import('@/mocks').then(({ initMocks }) => {
    void initMocks();
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...(isHealth100Client() && { retry: 2, retryDelay: 1000 }),
    },
  },
});

type AppLayoutProps = PropsWithChildren;

const AppLayout = ({ children }: AppLayoutProps): ReactElement => {
  const [loadingMocks, setLoadingMocks] = useState(environment.envName == AppEnvName.local);
  const pageTitle = useTranslation('page.title');

  useEffect(() => {
    if (environment.envName == AppEnvName.local) {
      import('@/mocks').then(({ initMocks }) => {
        initMocks().then(() => {
          setLoadingMocks(false);
        });
      });
    }
  }, []);

  useEffect(() => {
    document.title = `${pageTitle} | CVS`;
  }, [pageTitle]);

  if (loadingMocks) return <></>;

  return (
    <>
      <AppHeader
        pageName={pageTitle}
        targetComponent="main,mainContent,false"
        baseUrl={environment.defaultHeaderURL}
      />
      <AlertProvider>
        <QueryClientProvider client={queryClient}>
          {children}
          {!isMobileApp() && (
            <div className={styles.favoriteStoreContainer}>
              <YourStore />
            </div>
          )}
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
        </QueryClientProvider>
      </AlertProvider>
      <AppFooter pageName={pageTitle} baseUrl={environment.defaultHeaderURL} isAuthenticated={true} />
    </>
  );
};

export default withFeatureFlagProvider<AppLayoutProps>(AppLayout);
